/* ==========================================================================
   RBRTH — The Climb
   Scroll drives a camera up 3_mountain.glb along a Catmull-Rom path through the
   six stages authored in the scout tool. Behind the mountain, always present and
   mostly occluded by it, an aurora. At the summit the view opens onto a moon.

   Scroll is the only timeline. The pointer adds a small aim offset, never more
   than a couple of degrees, so the world breathes without fighting the scroll.
   ========================================================================== */

(function () {
  'use strict';

  var MODEL_URL = (window.RBRTH_CONFIG && window.RBRTH_CONFIG.modelUrl) || '3_mountain.glb';
  var STAGES_URL = (window.RBRTH_CONFIG && window.RBRTH_CONFIG.stagesUrl) || 'stages.json';

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  var renderer, scene, camera, sun, hemi, model;
  var posCurve, tgtCurve, stages = [];
  var aurora, moon;
  var raf = null, clock = null;

  /* eased scroll + pointer */
  var scrollTarget = 0, scrollEased = 0;
  var ptrTarget = { x: 0, y: 0 }, ptrEased = { x: 0, y: 0 };

  var $ = function (id) { return document.getElementById(id); };

  /* ====================================================== aurora shader ====
     Layered flowing bands. Vertical falloff keeps the feet soft so the curtain
     reads as light rather than as a sheet of geometry. Additive, so wherever the
     mountain is in front it simply occludes it. */

  var AURORA_VERT = [
    'varying vec2 vUv;',
    'void main() {',
    '  vUv = uv;',
    '  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);',
    '}'
  ].join('\n');

  var AURORA_FRAG = [
    'precision highp float;',
    'varying vec2 vUv;',
    'uniform float uTime;',
    'uniform float uProg;',     // 0 at base, 1 at summit — the curtain wakes up
    'uniform vec3  uGreen;',
    'uniform vec3  uPurple;',

    'vec2 hash2(vec2 p) {',
    '  p = vec2(dot(p, vec2(127.1, 311.7)), dot(p, vec2(269.5, 183.3)));',
    '  return -1.0 + 2.0 * fract(sin(p) * 43758.5453123);',
    '}',

    'float noise(vec2 p) {',
    '  vec2 i = floor(p), f = fract(p);',
    '  vec2 u = f * f * (3.0 - 2.0 * f);',
    '  return mix(mix(dot(hash2(i + vec2(0.0,0.0)), f - vec2(0.0,0.0)),',
    '                 dot(hash2(i + vec2(1.0,0.0)), f - vec2(1.0,0.0)), u.x),',
    '             mix(dot(hash2(i + vec2(0.0,1.0)), f - vec2(0.0,1.0)),',
    '                 dot(hash2(i + vec2(1.0,1.0)), f - vec2(1.0,1.0)), u.x), u.y);',
    '}',

    'float fbm(vec2 p) {',
    '  float v = 0.0, a = 0.5;',
    '  for (int i = 0; i < 5; i++) { v += a * noise(p); p *= 2.02; a *= 0.5; }',
    '  return v;',
    '}',

    /* ----------------------------------------------------------------------
       One ribbon. A continuous wave across the whole horizon — the light rises
       from a wandering base line and thins out above it. No gating along x, so
       the ribbon never breaks into dashes.

       fA/fB are integer frequencies on purpose: uv.x wraps around the cylinder,
       and only whole cycles meet cleanly at the seam.
       ---------------------------------------------------------------------- */
    'float ribbon(vec2 uv, float yc, float amp, float fA, float fB,',
    '             float phase, float speed, float thick) {',
    '  float x = uv.x * 6.28318530718;',
    '  float w = sin(x * fA + phase + uTime * speed) * amp',
    '          + sin(x * fB - phase * 1.7 - uTime * speed * 0.6) * amp * 0.45;',

    '  float base = yc + w;',
    '  float h = (uv.y - base) / thick;',
    '  if (h < 0.0) return 0.0;',                    // light travels upward only

    /* body: bright at the foot, exponential thinning upward */
    '  float body = exp(-h * 2.3) * smoothstep(0.0, 0.05, h);',

    /* striation, kept shallow (0.72 to 1.0) so the ribbon stays continuous */
    '  float rays = 0.72 + 0.28 * fbm(vec2(uv.x * 150.0 + phase * 20.0,',
    '                                      uv.y * 0.9 - uTime * speed * 1.4));',
    '  return body * rays;',
    '}',

    'void main() {',
    '  vec2 uv = vUv;',

    /* four ribbons, each its own height, wavelength, drift and thickness */
    '  float r1 = ribbon(uv, 0.46, 0.040, 2.0, 5.0,  0.0, 0.22, 0.115);',
    '  float r2 = ribbon(uv, 0.52, 0.032, 3.0, 7.0,  2.1, 0.17, 0.090);',
    '  float r3 = ribbon(uv, 0.58, 0.046, 1.0, 4.0,  4.3, 0.13, 0.130);',
    '  float r4 = ribbon(uv, 0.64, 0.028, 4.0, 9.0,  5.6, 0.09, 0.075);',

    '  float a = (r1 * 1.00 + r2 * 0.80 + r3 * 0.70 + r4 * 0.55) * 0.62;',

    /* green at the foot of the light, purple where it thins out — the real
       oxygen-green / nitrogen-violet split, driven by height above the base */
    '  float up = clamp((uv.y - 0.44) * 2.6, 0.0, 1.0);',
    '  vec3 col = mix(uGreen, uPurple, smoothstep(0.15, 0.95, up));',

    '  float head = 1.0 - smoothstep(0.80, 1.0, uv.y);',
    '  float sill = smoothstep(0.30, 0.44, uv.y);',
    '  float amp = mix(0.55, 1.0, uProg);',

    /* Additive again: alpha blending was only there to let the ribbons hide the
       stars, and the stars are gone. Additive over a black sky is what gave the
       curtain its glow. */
    '  gl_FragColor = vec4(col * a * head * sill * amp, 1.0);',
    '}'
  ].join('\n');

  /* ============================================================== setup === */

  function initRenderer() {
    var canvas = $('gl');
    renderer = new THREE.WebGLRenderer({ canvas: canvas, antialias: true, powerPreference: 'high-performance' });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    renderer.setSize(window.innerWidth, window.innerHeight);
    renderer.outputEncoding = THREE.sRGBEncoding;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.1;

    scene = new THREE.Scene();
    scene.background = new THREE.Color(0x050608);
    scene.fog = new THREE.FogExp2(0x070809, 0.018);

    camera = new THREE.PerspectiveCamera(70, window.innerWidth / window.innerHeight, 0.001, 400);

    hemi = new THREE.HemisphereLight(0x8fb9d0, 0x050505, 0.55);
    scene.add(hemi);
    sun = new THREE.DirectionalLight(0xdfe8ff, 2.1);
    scene.add(sun);

    window.addEventListener('resize', onResize);
  }

  function onResize() {
    camera.aspect = window.innerWidth / window.innerHeight;
    camera.updateProjectionMatrix();
    renderer.setSize(window.innerWidth, window.innerHeight);
  }

  function buildSky() {
    /* --- aurora: an open cylinder wrapped around the camera, so the curtain
       runs the full horizon instead of ending at the edges of a plane -------- */
    var geo = new THREE.CylinderGeometry(60, 60, 54, 96, 1, true);
    aurora = new THREE.Mesh(geo, new THREE.ShaderMaterial({
      vertexShader: AURORA_VERT,
      fragmentShader: AURORA_FRAG,
      uniforms: {
        uTime: { value: 0 },
        uProg: { value: 0 },
        uGreen: { value: new THREE.Color(0x3bf0a0) },
        uPurple: { value: new THREE.Color(0x9a4ff0) }
      },
      transparent: true,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      side: THREE.BackSide,            // we are inside the cylinder
      fog: false
    }));
    aurora.position.set(0, 20, 0);
    aurora.renderOrder = 2;
    scene.add(aurora);

    /* Stars removed on request. */

    /* --- moon: one disc, with a soft halo behind it ------------------------ */
    moon = new THREE.Group();
    var disc = new THREE.Mesh(
      new THREE.CircleGeometry(2.1, 48),
      new THREE.MeshBasicMaterial({ color: 0xf2efe6, fog: false, transparent: true })
    );
    var halo = new THREE.Mesh(
      new THREE.CircleGeometry(7.5, 48),
      new THREE.ShaderMaterial({
        vertexShader: 'varying vec2 vUv; void main(){ vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position,1.0); }',
        fragmentShader: [
          'precision mediump float; varying vec2 vUv;',
          'void main() {',
          '  float d = length(vUv - 0.5) * 2.0;',
          '  float a = exp(-d * 3.4) * 0.5;',
          '  gl_FragColor = vec4(vec3(0.85, 0.89, 0.95), a);',
          '}'
        ].join('\n'),
        transparent: true, blending: THREE.AdditiveBlending, depthWrite: false, fog: false
      })
    );
    halo.position.z = -0.1;
    moon.add(halo, disc);
    moon.position.set(-26, 26, -70);
    scene.add(moon);
  }

  /* ============================================================== snow === */

  var snowParticles = null;
  var snowGeo = null;
  var snowCount = 1200;
  var snowVelocities, snowPhases;

  function createSnowTexture() {
    var c = document.createElement('canvas');
    c.width = 32;
    c.height = 32;
    var ctx = c.getContext('2d');
    var g = ctx.createRadialGradient(16, 16, 0, 16, 16, 16);
    g.addColorStop(0, 'rgba(255, 255, 255, 0.95)');
    g.addColorStop(0.35, 'rgba(233, 228, 216, 0.65)');
    g.addColorStop(0.75, 'rgba(233, 228, 216, 0.15)');
    g.addColorStop(1, 'rgba(233, 228, 216, 0)');
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, 32, 32);
    var tex = new THREE.CanvasTexture(c);
    return tex;
  }

  function buildSnow() {
    snowGeo = new THREE.BufferGeometry();
    var positions = new Float32Array(snowCount * 3);
    snowVelocities = new Float32Array(snowCount * 3);
    snowPhases = new Float32Array(snowCount);

    var spread = 45;
    var height = 50;

    for (var i = 0; i < snowCount; i++) {
      var i3 = i * 3;
      positions[i3] = (Math.random() - 0.5) * spread;
      positions[i3 + 1] = Math.random() * height;
      positions[i3 + 2] = (Math.random() - 0.5) * spread;

      snowVelocities[i3] = (Math.random() - 0.5) * 0.012;
      snowVelocities[i3 + 1] = -(0.035 + Math.random() * 0.045);
      snowVelocities[i3 + 2] = (Math.random() - 0.5) * 0.012;

      snowPhases[i] = Math.random() * Math.PI * 2;
    }

    snowGeo.setAttribute('position', new THREE.BufferAttribute(positions, 3));

    var snowMat = new THREE.PointsMaterial({
      color: 0xffffff,
      size: 0.35,
      map: createSnowTexture(),
      transparent: true,
      opacity: 0.65,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      fog: true
    });

    snowParticles = new THREE.Points(snowGeo, snowMat);
    snowParticles.renderOrder = 3;
    scene.add(snowParticles);
  }

  function updateSnow(time, tClimb) {
    if (!snowParticles || !snowGeo) return;

    var pos = snowGeo.attributes.position.array;
    var camX = camera.position.x;
    var camY = camera.position.y;
    var camZ = camera.position.z;

    var spread = 40;
    var yBox = 35;
    var speedMul = 1.0 + (tClimb || 0) * 0.5;

    for (var i = 0; i < snowCount; i++) {
      var i3 = i * 3;
      var phase = snowPhases[i];

      pos[i3] += Math.sin(time * 0.7 + phase) * 0.01 + snowVelocities[i3];
      pos[i3 + 1] += snowVelocities[i3 + 1] * speedMul;
      pos[i3 + 2] += Math.cos(time * 0.5 + phase) * 0.01 + snowVelocities[i3 + 2];

      if (pos[i3 + 1] < camY - yBox * 0.5) {
        pos[i3 + 1] = camY + yBox * 0.5;
        pos[i3] = camX + (Math.random() - 0.5) * spread;
        pos[i3 + 2] = camZ + (Math.random() - 0.5) * spread;
      } else if (pos[i3 + 1] > camY + yBox * 0.5) {
        pos[i3 + 1] = camY - yBox * 0.5;
      }

      if (Math.abs(pos[i3] - camX) > spread * 0.5) {
        pos[i3] = camX + (Math.random() - 0.5) * spread;
      }
      if (Math.abs(pos[i3 + 2] - camZ) > spread * 0.5) {
        pos[i3 + 2] = camZ + (Math.random() - 0.5) * spread;
      }
    }

    snowGeo.attributes.position.needsUpdate = true;
  }

  /* ============================================================== stages == */

  function buildPath(data) {
    stages = data.stages;
    var pts = stages.map(function (s) { return new THREE.Vector3().fromArray(s.position); });
    var tgs = stages.map(function (s) { return new THREE.Vector3().fromArray(s.target); });

    // centripetal avoids the overshoot a uniform spline gives on uneven spacing
    posCurve = new THREE.CatmullRomCurve3(pts, false, 'centripetal', 0.5);
    tgtCurve = new THREE.CatmullRomCurve3(tgs, false, 'centripetal', 0.5);

    liftPathAboveTerrain();
  }

  /* ------------------------------------------------------- terrain clamp --
     The spline between captured stages can dip below the rock — around stage 3
     the camera passed through the face. Rather than hand-tuning the stages, walk
     the path once at load, drop a ray onto the model under each sample, and
     raise any sample that sits below the surface plus a clearance. Done once, so
     the per-frame cost stays zero. */

  var CLEARANCE = 0.055;

  function liftPathAboveTerrain() {
    if (!model) return;

    var SAMPLES = 160;
    var ray = new THREE.Raycaster();
    ray.firstHitOnly = true;
    var down = new THREE.Vector3(0, -1, 0);
    var pts = [], lifted = 0;

    for (var i = 0; i <= SAMPLES; i++) {
      var t = i / SAMPLES;
      var p = posCurve.getPointAt(t).clone();

      // start well above, look down, take the highest surface under this point
      ray.set(new THREE.Vector3(p.x, p.y + 4, p.z), down);
      var hit = ray.intersectObject(model, true)[0];

      if (hit && p.y < hit.point.y + CLEARANCE) {
        p.y = hit.point.y + CLEARANCE;
        lifted++;
      }
      pts.push(p);
    }

    if (!lifted) return;

    // smooth the lifted path so the correction doesn't read as a step
    for (var pass = 0; pass < 3; pass++) {
      for (var j = 1; j < pts.length - 1; j++) {
        pts[j].y = (pts[j - 1].y + pts[j].y * 2 + pts[j + 1].y) * 0.25;
      }
    }

    posCurve = new THREE.CatmullRomCurve3(pts, false, 'centripetal', 0.5);
    console.info('[climb] raised ' + lifted + '/' + (SAMPLES + 1) + ' path samples above the rock');
  }

  /* sun/exposure are per-stage values, so lerp them by where t sits */
  function stageBlend(t) {
    var f = t * (stages.length - 1);
    var i = Math.min(stages.length - 2, Math.floor(f));
    var k = f - i;
    var a = stages[i], b = stages[i + 1];
    return {
      sunAz: a.sunAz + (b.sunAz - a.sunAz) * k,
      sunEl: a.sunEl + (b.sunEl - a.sunEl) * k,
      exposure: a.exposure + (b.exposure - a.exposure) * k,
      fov: a.fov + (b.fov - a.fov) * k,
      fog: a.fog + (b.fog - a.fog) * k
    };
  }

  function placeSun(azDeg, elDeg) {
    var az = azDeg * Math.PI / 180, el = elDeg * Math.PI / 180, r = 30;
    sun.position.set(
      r * Math.cos(el) * Math.cos(az),
      r * Math.sin(el),
      r * Math.cos(el) * Math.sin(az)
    );
  }

  /* ============================================================== model === */

  function loadModel() {
    return new Promise(function (resolve, reject) {
      new THREE.GLTFLoader().load(MODEL_URL, function (gltf) {
        model = gltf.scene;
        model.traverse(function (o) {
          if (!o.isMesh) return;
          o.material.side = THREE.FrontSide;   // authored doubleSided; halves overdraw
          o.frustumCulled = true;
        });
        scene.add(model);
        resolve();
      }, function (e) {
        /* progress is not shown: the splash plays its own intro */
      }, reject);
    });
  }

  /* ============================================================== scroll == */

  /* Inner pages park the camera at a fixed stage instead of climbing, so the
     same world carries across the site: <body data-stage="3">. The homepage
     leaves it unset and scroll drives the full ascent. */
  var PARK = document.body.getAttribute('data-stage');
  PARK = PARK === null ? null : Math.max(0, Math.min(1, (+PARK - 1) / 5));

  function readScroll() {
    if (PARK !== null) { scrollTarget = PARK; return; }
    var max = document.documentElement.scrollHeight - window.innerHeight;
    scrollTarget = max > 0 ? Math.min(1, Math.max(0, window.scrollY / max)) : 0;
  }

  /* ================================================================ loop == */

  var _p = new THREE.Vector3(), _t = new THREE.Vector3();

  function apply(t) {
    posCurve.getPointAt(t, _p);
    tgtCurve.getPointAt(t, _t);

    var b = stageBlend(t);

    camera.position.copy(_p);

    /* pointer nudges the aim by about a degree and a half, scaled by distance so
       it reads the same whether the target is near or far */
    var aim = _t.clone();
    var dist = aim.distanceTo(_p);
    aim.x += ptrEased.x * dist * 0.026;
    aim.y += -ptrEased.y * dist * 0.018;
    camera.lookAt(aim);

    if (Math.abs(camera.fov - b.fov) > 0.01) {
      camera.fov = b.fov;
      camera.updateProjectionMatrix();
    }

    placeSun(b.sunAz, b.sunEl);
    renderer.toneMappingExposure = b.exposure;
    scene.fog.density = b.fog;

    /* sky wakes up over the back half of the climb */
    var wake = Math.pow(Math.max(0, (t - 0.12) / 0.88), 1.2);
    aurora.material.uniforms.uProg.value = 0.35 + wake * 0.65;
    moon.children.forEach(function (m) { m.material.opacity = wake; });
    moon.visible = wake > 0.01;

    var rail = $('progressFill');
    if (rail) rail.style.transform = 'scaleX(' + (PARK === null ? t : 0).toFixed(4) + ')';
  }

  function frame() {
    raf = requestAnimationFrame(frame);

    scrollEased += (scrollTarget - scrollEased) * 0.075;
    ptrEased.x += (ptrTarget.x - ptrEased.x) * 0.045;
    ptrEased.y += (ptrTarget.y - ptrEased.y) * 0.045;

    var tm = clock.getElapsedTime();
    aurora.material.uniforms.uTime.value = tm;

    /* sky rides with the camera so it never runs out; only x/z follow, the
       curtain keeps its own height */
    aurora.position.x = camera.position.x;
    aurora.position.z = camera.position.z;

    updateSnow(tm, scrollEased);

    apply(scrollEased);
    renderer.render(scene, camera);
  }

  function renderOnce() {
    scrollEased = scrollTarget;
    apply(scrollEased);
    renderer.render(scene, camera);
  }


  /* ============================================================= splash ==
     The theme's behaviour: run the line-draw, then lift once BOTH the intro has
     played and the page has loaded. If the model is still streaming when the
     intro ends, "Hustler" bounces as a loading cue rather than lifting early. */

  function initSplash() {
    var s = document.getElementById('splash');
    if (!s) return { done: function () {} };

    /* ?nosplash skips the intro — for screenshots and quick layout checks */
    if (reduceMotion.matches || location.search.indexOf('nosplash') > -1) {
      s.classList.add('lift');
      s.style.display = 'none';
      return { done: function () {} };
    }

    requestAnimationFrame(function () { s.classList.add('run'); });

    var minDone = false, assetsDone = false;
    function tryLift() {
      if (minDone && assetsDone) {
        s.classList.remove('waiting');
        s.classList.add('lift');
      }
    }
    setTimeout(function () { minDone = true; if (!assetsDone) s.classList.add('waiting'); tryLift(); }, 4800);
    setTimeout(function () { minDone = true; assetsDone = true; tryLift(); }, 12000); // safety cap
    s.addEventListener('transitionend', function () {
      if (s.classList.contains('lift')) s.style.display = 'none';
    });

    return { done: function () { assetsDone = true; tryLift(); } };
  }

  /* ============================================================= reveals == */

  function wireReveals() {
    // entrance animation removed; sections render as-is
  }

  /* ============================================================== chart ==
     The theme draws both lines with GSAP ScrollTrigger. Same behaviour here
     without the dependency: progress is the chart's own travel through the
     viewport, the stroke is revealed by dashoffset, and each dot rides the end
     of its line. */

  function wireChart() {
    var frame = document.querySelector('[data-chart]');
    if (!frame) return;

    var pairs = [
      [frame.querySelector('[data-path-coffee]'), frame.querySelector('[data-dot-coffee]')],
      [frame.querySelector('[data-path-rbrth]'), frame.querySelector('[data-dot-rbrth]')]
    ].filter(function (p) { return p[0] && p[1]; });
    if (!pairs.length) return;

    pairs.forEach(function (p) {
      var len = p[0].getTotalLength();
      p[0].style.strokeDasharray = len;
      p[0].style.strokeDashoffset = len;
    });

    function draw() {
      var r = frame.getBoundingClientRect();
      var vh = window.innerHeight;
      // 0 as the chart enters from the bottom, 1 once it has risen into view
      var t = (vh * 0.92 - r.top) / (vh * 0.62);
      t = Math.max(0, Math.min(1, t));

      pairs.forEach(function (pair) {
        var path = pair[0], dot = pair[1];
        var len = path.getTotalLength();
        path.style.strokeDashoffset = len * (1 - t);
        var pt = path.getPointAtLength(len * Math.max(0.0001, t));
        dot.setAttribute('cx', pt.x);
        dot.setAttribute('cy', pt.y);
        dot.style.opacity = t > 0.02 ? 1 : 0;
      });
    }

    window.addEventListener('scroll', draw, { passive: true });
    window.addEventListener('resize', draw);
    draw();
  }

  /* ============================================================== drift ==
     Hovering a surface nudges it a few pixels toward the pointer and eases it
     back on leave. The motion is deliberately small: it should read as the
     panel settling under your attention, not as a tilt effect. */

  function wireDrift() {
    if (reduceMotion.matches || !window.matchMedia('(pointer: fine)').matches) return;

    var MAX = 5;   // px

    document.querySelectorAll('.glass:not(.is-glass)').forEach(function (el) {
      el.addEventListener('pointermove', function (e) {
        var r = el.getBoundingClientRect();
        var dx = (e.clientX - (r.left + r.width / 2)) / (r.width / 2);
        var dy = (e.clientY - (r.top + r.height / 2)) / (r.height / 2);
        el.style.setProperty('--hx', (dx * MAX).toFixed(2) + 'px');
        el.style.setProperty('--hy', (dy * MAX - 3).toFixed(2) + 'px');
      });
      el.addEventListener('pointerleave', function () {
        el.style.setProperty('--hx', '0px');
        el.style.setProperty('--hy', '0px');
      });
    });
  }

  /* =============================================================== shop == */

  function wireShop() {
    /* add-to-cart and the counter now live in cart.js; only page-local bits here */
    var news = document.getElementById('news');
    if (news) news.addEventListener('submit', function (e) {
      e.preventDefault();
      var note = document.getElementById('newsNote');
      if (note) note.textContent = 'You are on the list.';
      news.reset();
    });

    var burger = document.getElementById('hamburger');
    if (burger) burger.addEventListener('click', function () {
      var open = burger.getAttribute('aria-expanded') === 'true';
      burger.setAttribute('aria-expanded', String(!open));
      document.body.classList.toggle('nav-open', !open);
    });

    var track = document.getElementById('journalTrack');
    var jPrev = document.getElementById('journalPrev');
    var jNext = document.getElementById('journalNext');
    if (track && jPrev && jNext) {
      jPrev.addEventListener('click', function () {
        var card = track.querySelector('.post');
        var step = card ? card.offsetWidth + 20 : 320;
        track.scrollBy({ left: -step, behavior: 'smooth' });
      });
      jNext.addEventListener('click', function () {
        var card = track.querySelector('.post');
        var step = card ? card.offsetWidth + 20 : 320;
        track.scrollBy({ left: step, behavior: 'smooth' });
      });
    }
  }

  /* ================================================================ boot == */

  function boot() {
    var splash = initSplash();
    wireReveals();
    wireShop();
    wireDrift();
    wireChart();
    initRenderer();
    buildSky();
    buildSnow();
    clock = new THREE.Clock();

    Promise.all([
      fetch(STAGES_URL).then(function (r) { return r.json(); }),
      loadModel()
    ]).then(function (res) {
      buildPath(res[0]);

      splash.done();
      document.body.classList.add('ready');

      readScroll();
      if (PARK !== null) scrollEased = PARK;
      window.addEventListener('scroll', readScroll, { passive: true });

      if (!reduceMotion.matches && window.matchMedia('(pointer: fine)').matches) {
        window.addEventListener('pointermove', function (e) {
          ptrTarget.x = (e.clientX / window.innerWidth) * 2 - 1;
          ptrTarget.y = (e.clientY / window.innerHeight) * 2 - 1;
        }, { passive: true });
      }

      if (reduceMotion.matches) {
        renderOnce();
        window.addEventListener('scroll', renderOnce, { passive: true });
        window.addEventListener('resize', renderOnce);
        return;
      }

      document.addEventListener('visibilitychange', function () {
        if (document.hidden) { if (raf) cancelAnimationFrame(raf); raf = null; }
        else if (!raf) { clock.getDelta(); frame(); }
      });

      frame();
    }).catch(function (err) {
      console.error('[climb] ' + err);
      splash.done();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
